import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import type { CooldownData } from '../../businessLogicService/brainBreakCooldown';
import type { BrainBreakPlacement } from '../../models/BrainBreak';

export interface BrainBreakSession {
  hasSeenBrainBreak: boolean;
  assignedPlacement: BrainBreakPlacement | null;
  cooldownData: CooldownData | undefined;
  isHydrated: boolean;
}

export interface BrainBreakModalState {
  isOpen: boolean;
  currentPlacement: BrainBreakPlacement | null;
}

export interface BrainBreakReduxState {
  value: {
    session: BrainBreakSession;
    modal: BrainBreakModalState;
  };
}

const initialState: BrainBreakReduxState = {
  value: {
    session: {
      hasSeenBrainBreak: false,
      assignedPlacement: null,
      cooldownData: undefined,
      isHydrated: false,
    },
    modal: {
      isOpen: false,
      currentPlacement: null,
    },
  },
};

export const brainBreakSlice = createSlice({
  name: 'brainBreak',
  initialState,
  reducers: {
    // Session actions
    hydrateBrainBreakSession: (
      state,
      action: PayloadAction<{ cooldownData: CooldownData | undefined; eligible: boolean }>,
    ) => {
      state.value.session.cooldownData = action.payload.cooldownData;
      state.value.session.isHydrated = true;
      // If not eligible due to cooldown, mark as seen
      if (!action.payload.eligible) {
        state.value.session.hasSeenBrainBreak = true;
      }
    },
    setAssignedPlacement: (state, action: PayloadAction<BrainBreakPlacement>) => {
      state.value.session.assignedPlacement = action.payload;
    },
    markBrainBreakViewed: state => {
      state.value.session.hasSeenBrainBreak = true;
    },
    resetBrainBreakSession: state => {
      state.value.session = initialState.value.session;
      state.value.modal = initialState.value.modal;
    },
    // Modal actions
    showBrainBreakModal: (state, action: PayloadAction<BrainBreakPlacement>) => {
      state.value.modal.isOpen = true;
      state.value.modal.currentPlacement = action.payload;
    },
    closeBrainBreakModal: state => {
      state.value.modal.isOpen = false;
      state.value.modal.currentPlacement = null;
      state.value.session.hasSeenBrainBreak = true;
    },
  },
});

// Selectors
export const selectBrainBreakSession = (state: RootState) => state.brainBreak.value.session;
export const selectBrainBreakModal = (state: RootState) => state.brainBreak.value.modal;
export const selectAssignedPlacement = (state: RootState) => state.brainBreak.value.session.assignedPlacement;

// Actions
export const {
  hydrateBrainBreakSession,
  setAssignedPlacement,
  markBrainBreakViewed,
  resetBrainBreakSession,
  showBrainBreakModal,
  closeBrainBreakModal,
} = brainBreakSlice.actions;

export default brainBreakSlice.reducer;
